import { FC, useState } from 'react';

// material-ui imports
import { alpha, useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PreviewIcon from '@mui/icons-material/Preview';

// other imports
import Avatar from 'ui-component/extended/Avatar';
import { ThemeMode } from 'config';
import BlockTwoToneIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import { addCommasToNumbers, getBackgroundColor } from 'Application/utils';
import { usePolicyStore } from 'Application/store';
import { toast } from 'sonner';
import { GetAgencyPoliciesResponse, PolicyStatus } from 'Application/models';
import { useNavigate } from 'react-router';

interface CompletedTtransactionsListProps {
    policies: GetAgencyPoliciesResponse;
    regionName: string;
    identificationType: string;
    currency: string;
    handleRedirect: (policyNumber: string) => void; // Function type
}

const CompletedTransactionsList: FC<CompletedTtransactionsListProps> = ({ policies, currency, identificationType, handleRedirect }) => {
    const theme = useTheme();
    const navigate = useNavigate();

    // Local state for handling download action
    const [downloading, setDownloading] = useState<string | null>(null);

    const { getPolicyDocument, isError, error } = usePolicyStore((state) => ({
        getPolicyDocument: state.getPolicyDocument,
        isError: state.isError,
        error: state.error
    }));

    const handleDownloadPolicy = async (policyNumber: string) => {
        setDownloading(policyNumber); // Start downloading the specific policy
        try {
            toast.info('Downloading policy document...');

            // Use the policy number to get the policy document
            const policyDocumentBlob = await getPolicyDocument(policyNumber);

            const url = window.URL.createObjectURL(policyDocumentBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Policy_${policyNumber}.pdf`; // Use the passed policy number

            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            toast.success('Policy document downloaded successfully');
        } catch (error) {
            console.error(error);
            toast.error('Failed to download the policy document.');
        } finally {
            setDownloading(null); // Reset downloading state after the operation
        }
    };

    // const handleRedirect = (policyNumber: string) => {
    //     console.log('policyNumber being passed', policyNumber);
    //     navigate(`/agency/viewpolicy/${policyNumber}`);
    // };

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ pl: 3 }}>Policy No.</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>{identificationType}</TableCell>
                        <TableCell>Departure Date</TableCell>
                        <TableCell>Return Date</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Amount {currency}</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell>Issued By</TableCell>

                        <TableCell align="center" sx={{ pr: 3 }}>
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {policies && Array.isArray(policies.content) && policies.content.length > 0 ? (
                        policies.content.map((row, index) => (
                            <TableRow hover key={index}>
                                <TableCell sx={{ pl: 3 }}>{row.policyNumber}</TableCell>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Avatar
                                            alt="User Avatar"
                                            className={`border border-gray-200 ${
                                                row.quotation?.customer?.firstName
                                                    ? getBackgroundColor(row.quotation?.customer?.firstName[0].toUpperCase())
                                                    : 'transparent'
                                            }`}
                                        >
                                            {row.quotation?.customer?.firstName ? row.quotation.customer.firstName[0].toUpperCase() : ''}
                                        </Avatar>
                                        <Stack>
                                            <Typography variant="subtitle1">
                                                {row.quotation?.customer?.firstName || ''} {row.quotation?.customer?.otherNames || ''}
                                            </Typography>
                                            <Typography variant="subtitle2" noWrap>
                                                {row.quotation?.customer?.emailAddress || ''}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </TableCell>
                                <TableCell>{row.quotation?.customer.identificationNumber}</TableCell>
                                <TableCell>{row.quotation?.departureDate}</TableCell>
                                <TableCell>{row.quotation?.returnDate}</TableCell>
                                <TableCell>{row.quotation?.travelPlan?.name}</TableCell>
                                <TableCell>{addCommasToNumbers(row.payment?.amount.toString())}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={row.policyStatus.toTitle()}
                                        size="small"
                                        sx={
                                            row.policyStatus === PolicyStatus.CONFIRMED
                                                ? {
                                                      bgcolor:
                                                          theme.palette.mode === ThemeMode.DARK
                                                              ? 'dark.main'
                                                              : alpha(theme.palette.success.light, 0.6),
                                                      color: 'success.dark'
                                                  }
                                                : {
                                                      bgcolor:
                                                          theme.palette.mode === ThemeMode.DARK
                                                              ? 'dark.main'
                                                              : alpha(theme.palette.error.light, 0.6),
                                                      color: 'error.dark'
                                                  }
                                        }
                                    />
                                </TableCell>
                                <TableCell>{row.createdAt ? format(new Date(row.createdAt), 'MM-dd-yyyy') : 'N/A'}</TableCell>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Avatar
                                            alt="User Avatar"
                                            className={`border border-gray-200 ${
                                                row.issuerName ? getBackgroundColor(row.issuerName[0].toUpperCase()) : 'transparent'
                                            }`}
                                        >
                                            {row.issuerName ? row.issuerName[0].toUpperCase() : ''}
                                        </Avatar>
                                        <Stack>
                                            <Typography variant="subtitle1">{row.issuerName || ''}</Typography>
                                            <Typography variant="subtitle2" noWrap>
                                                {row.issuerEmailAddress || ''}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </TableCell>
                                <TableCell align="center" sx={{ pr: 3 }}>
                                    <Stack direction="row" justifyContent="center" alignItems="center">
                                        <Tooltip placement="top" title="View Policy">
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleRedirect(row.policyNumber)} // Pass the policyNumber here
                                                // onClick={() => console.log('View Policy Button Clicked', row.policyNumber)} // Pass the policyNumber here
                                                size="large"
                                            >
                                                <PreviewIcon sx={{ fontSize: '1.1rem', ml: 1 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip placement="top" title="Download Policy Document">
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleDownloadPolicy(row.policyNumber)} // Pass policy number here
                                                disabled={downloading === row.policyNumber} // Disable if that policy is downloading
                                                sx={{
                                                    color: downloading === row.policyNumber ? 'grey.400' : 'orange.dark',
                                                    borderColor: 'orange.main',
                                                    '&:hover': { bgcolor: 'orange.light' }
                                                }}
                                                size="large"
                                            >
                                                <BlockTwoToneIcon sx={{ fontSize: '1.1rem', ml: 1 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={12}>
                                <Typography variant="subtitle1" align="center">
                                    No policies available
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CompletedTransactionsList;
